import React from 'react';

import Wahanda from 'common/wahanda';
import { MonthlySalesReportAnalytics } from 'common/analytics';
import { buildSalesReportDataCells } from '../common/buildSalesReportDataCells';
import { salesReportHeaderRows, SalesReportMetrics } from '../common/salesReportTableLayout';
import SalesReportHashLink from '../SalesReport/hashLink';
import type { SharedProps } from 'reconnect-types/moduleFederation';

type MonthlySalesRow = SalesReportMetrics & {
  month: any;
};

type MonthlySalesReportData = {
  rows: MonthlySalesRow[];
  total: SalesReportMetrics;
};

export type MonthlySalesReportTableProps = {
  venueId: string;
  employeeFilter: number;
  selectedChannel: string;
  reportData: MonthlySalesReportData;
};

const monthlySalesDataCellTestIds = {
  servicesQty: 'monthly-sales-services-qty',
  servicesGross: 'monthly-sales-services-gross',
  productsQty: 'monthly-sales-products-qty',
  productsGross: 'monthly-sales-products-gross',
  voucherQuantity: 'monthly-sales-vouchers-qty',
  voucherGross: 'monthly-sales-vouchers-gross',
  prepaid: 'monthly-sales-prepaid',
  total: 'monthly-sales-gross-total',
};

export const getMonthlySalesReportTableProps = ({
  venueId,
  employeeFilter,
  selectedChannel,
  reportData,
}: MonthlySalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.monthlySalesReport.headings;

  const headerRows = salesReportHeaderRows({
    firstColumn: headings.month,
    services: headings.services,
    products: headings.products,
    vouchers: headings.vouchers,
    // `prepaid` is only defined under productSalesReport in the locale files.
    prepaid: Wahanda.lang.reports.sales.productSalesReport.headings.prepaid,
    total: headings.totalSalesIncludingVat,
    quantity: headings.quantity,
    sales: headings.salesIncludingVat,
  });

  const tableRows = reportData.rows.map((row) => {
    const rowHeading = row.month.format(headings.dateFormat);
    const navLinkProps = {
      venueId,
      fromDate: row.month.clone().startOf('month'),
      toDate: row.month.clone().endOf('month'),
      employeeFilter,
      selectedChannel,
    };

    return {
      id: rowHeading,
      dataTestId: 'monthly-sales-row',
      cells: [
        {
          content: (
            <SalesReportHashLink
              {...navLinkProps}
              onClick={MonthlySalesReportAnalytics.trackDateLinkClick}
            >
              {rowHeading}
            </SalesReportHashLink>
          ),
          dataTestId: 'monthly-sales-date',
        },
        ...buildSalesReportDataCells(row, monthlySalesDataCellTestIds, {
          ...navLinkProps,
          onServiceLinkClick: MonthlySalesReportAnalytics.trackLinkToServicesSalesClick,
          onProductLinkClick: MonthlySalesReportAnalytics.trackLinkToProductsSalesClick,
        }),
      ],
    };
  });

  const footerRow = {
    cells: [
      { content: headings.total },
      ...buildSalesReportDataCells(reportData.total, monthlySalesDataCellTestIds).map((cell) => ({
        ...cell,
        variant: 'grandTotal' as const,
      })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'monthly-sales-totals',
    testID: 'monthly-sales-report-table',
  };
};
